import React, { useEffect, useState } from "react";
import { Spin, Avatar, Collapse, Switch, Divider } from "antd";
import clsx from "clsx";
import ReactHtmlParser from "html-react-parser";
import { useIntl } from "react-intl";
import styled from "styled-components";
import {
  CheckOutlined,
} from "@ant-design/icons";
import * as constants from "../../constants";
import { getSecuredUrl } from "../../utils/utilities";
import StyledText from "../../components/StyledText";
import MediaCarousel from "../../components/MediaCarousel";
import {
  programDataSelector,
  opportunityDataSelector,
  toggleSpinnerSelector,
  savedContentDataSelector,
  donationDataSelector,
  indicatorDataSelector
} from "../../redux/selectors";
import {
  useAppSelector
} from "../../redux/store";
import CharityFinalPreviewIndicators from "./CharityFinalPreviewIndicators";
import grantIcon from "../../assets/grant.svg";
import HorizontalGraph from "../../components/HorizontalGraph";
import environmentIcon from "../../assets/environmentIcon.svg";
import socialIcon from "../../assets/socialIcon.svg";
import inkind from "../../assets/inkind.svg";
import volunteer from "../../assets/volunteer.svg";
import goal1 from "../../assets/goal1.png";
import goal2 from "../../assets/goal2.png";
import goal3 from "../../assets/goal3.png";
import goal4 from "../../assets/goal4.png";
import goal5 from "../../assets/goal5.png";
import goal6 from "../../assets/goal6.png";
import goal7 from "../../assets/goal7.png";
import goal8 from "../../assets/goal8.png";
import goal9 from "../../assets/goal9.png";
import goal10 from "../../assets/goal10.png";
import goal11 from "../../assets/goal11.png";
import goal12 from "../../assets/goal12.png";
import goal13 from "../../assets/goal13.png";
import goal14 from "../../assets/goal14.png";
import goal15 from "../../assets/goal15.png";
import goal16 from "../../assets/goal16.png";
import goal17 from "../../assets/goal17.png";
import goalInverted1 from "../../assets/goalInverted1.png";
import goalInverted2 from "../../assets/goalInverted2.png";
import goalInverted3 from "../../assets/goalInverted3.png";
import goalInverted4 from "../../assets/goalInverted4.png";
import goalInverted5 from "../../assets/goalInverted5.png";
import goalInverted6 from "../../assets/goalInverted6.png";
import goalInverted7 from "../../assets/goalInverted7.png";
import goalInverted8 from "../../assets/goalInverted8.png";
import goalInverted9 from "../../assets/goalInverted9.png";
import goalInverted10 from "../../assets/goalInverted10.png";
import goalInverted11 from "../../assets/goalInverted11.png";
import goalInverted12 from "../../assets/goalInverted12.png";
import goalInverted13 from "../../assets/goalInverted13.png";
import goalInverted14 from "../../assets/goalInverted14.png";
import goalInverted15 from "../../assets/goalInverted15.png";
import goalInverted16 from "../../assets/goalInverted16.png";
import goalInverted17 from "../../assets/goalInverted17.png";
import file from "../../assets/file.svg";

const goals = [{
  key: 1,
  image: goalInverted1,
  selected: goal1
},
{
  key: 2,
  image: goalInverted2,
  selected: goal2
},
{
  key: 3,
  image: goalInverted3,
  selected: goal3
},
{
  key: 4,
  image: goalInverted4,
  selected: goal4
},
{
  key: 5,
  image: goalInverted5,
  selected: goal5
},
{
  key: 6,
  image: goalInverted6,
  selected: goal6
},
{
  key: 7,
  image: goalInverted7,
  selected: goal7
},
{
  key: 8,
  image: goalInverted8,
  selected: goal8
},
{
  key: 9,
  image: goalInverted9,
  selected: goal9
},
{
  key: 10,
  image: goalInverted10,
  selected: goal10
},
{
  key: 11,
  image: goalInverted11,
  selected: goal11
},
{
  key: 12,
  image: goalInverted12,
  selected: goal12
},
{
  key: 13,
  image: goalInverted13,
  selected: goal13
},
{
  key: 14,
  image: goalInverted14,
  selected: goal14
},
{
  key: 15,
  image: goalInverted15,
  selected: goal15
},
{
  key: 16,
  image: goalInverted16,
  selected: goal16
},
{
  key: 17,
  image: goalInverted17,
  selected: goal17
}
]

const getUniqueSortedOutcomeUNSDG = (objectives, isProgramCustom) => {
  const outcomes = objectives?.flatMap(objective =>
    isProgramCustom ?
      objective?.objectivesOutcome?.flatMap(outcome => outcome?.outcomeUNSDG) :
      objective?.objectiveUNSDG
  ) || [];

  const uniqueSortedOutcomes = Array.from(new Set(outcomes)).sort((a, b) => a - b);

  return uniqueSortedOutcomes.map(el => {
    const selectedOutcomes = goals.find(gl => gl.key === el);
    return {
      key: el,
      image: selectedOutcomes?.image,
      selected: selectedOutcomes?.selected,
    };
  });
};

const ProgramReview = ({ className }) => {
  const baseClassName = clsx("programReview", className);
  const intl = useIntl();
  const [selectedName, setSelectedName] = useState('');
  const [documentUrls, setDocumentUrls] = useState({});
  const [showPlanned, setShowPlanned] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const [selectedUnsdgs, setSelectedUnsdgs] = useState([]);
  const [mediaImages, setMediaImages] = useState([]);
  const showSpinner = useAppSelector(toggleSpinnerSelector);
  const opportunityData = useAppSelector(opportunityDataSelector) || {}
  const programData = useAppSelector(programDataSelector);
  const donationData = useAppSelector(donationDataSelector);
  const indicators = useAppSelector(indicatorDataSelector) || {};

  const separateBySpecificTypes = (data) => {
    if (!data?.length) return
    return data?.reduce((acc, item) => {
      if (['Grant', 'Volunteering', 'In-kind']?.includes(item.type)) {
        acc[item.type]?.push(item);
      }
      return acc;
    }, { Grant: [], Volunteering: [], 'In-kind': [] });
  }

  const { opportunity } = donationData?.[0] || {};

  const { Grant, Volunteering, 'In-kind': InKind } = separateBySpecificTypes(opportunity?.[0]?.donations) || {};

  const {
    outcomeMethod
  } = programData || {};

  const isProgramCustom = outcomeMethod === "Programme Custom";

  const {
    whatHappensNext,
    customContentAnswer,
    additionalComment,
    gallery,
    goalofCompanyProgramme,
    caseStudy,
    beneficiariesType,
    totalDirect,
    totalIndirect,
    beneficiariesChanged,
    actualEnvironmentalMeasure,
    environmentMeasuresChange,
    totalOutputs,
    differentOutputs,
    inputsChange,
    actualDonationUse,
    actualVolunteerUse,
    actualInKindUse,
    uploadedDocuments
  } = useAppSelector(savedContentDataSelector) || {}

  const { opportunity:
    { opportunitySummary,
      cashTargetCurrencyCode,
      environmentalMeasure,
      expectedOutput,
      volunteerUse,
      donationUse,
      inKindUse,
      opportunityBeneficiaries,
      indirectBeneficiaries,
      directBeneficiaries,
      impactApproach: { objectives } = {},
    } = {} }
    = opportunityData || {};

  const goals = getUniqueSortedOutcomeUNSDG(objectives, isProgramCustom);

  useEffect(() => {
    const fetchDocumentUrls = async () => {
      try {
        if (caseStudy?.length > 0) {
          const promises = caseStudy.map(async (el) => {
            if (el?.documents?.length > 0 && el?.outcome && !documentUrls[el.outcome]?.length) {
              const documentPromises = el.documents.map(async (doc) => {
                const key = doc.key || doc.fileKey
                const securedURI = await getSecuredUrl(key);
                const securedURL = URL.createObjectURL(securedURI);
                return { url: securedURL, name: doc.name, type: doc.type };
              });

              const resolvedDocuments = await Promise.all(documentPromises);
              return { outcome: el?.outcome, documents: resolvedDocuments.filter(Boolean) };
            }
            return null;
          });

          const resolvedDocumentData = await Promise.all(promises);
          const validDocuments = resolvedDocumentData.filter(Boolean);

          setDocumentUrls((prev) => ({
            ...prev,
            ...validDocuments.reduce((acc, { outcome, documents }) => {
              acc[outcome] = documents;
              return acc;
            }, {}),
          }));
        }
      } catch (error) {
        console.error('Error fetching document URLs:', error);
      }
    };

    fetchDocumentUrls();
  }, [caseStudy]);

  useEffect(() => {
    const fetchMediaImages = async () => {
      try {
        if (gallery?.length > 0 && !mediaImages?.length) {
          const promises = gallery.map(async (file) => {
            const key = file?.key || file?.fileKey;
            const securedURI = await getSecuredUrl(key);
            const securedURL = URL.createObjectURL(securedURI);
            return { url: securedURL, name: file.name, mediaDescription: file.mediaDescription };
          });
          const resolvedMediaImages = await Promise.all(promises);
          const validMediaImages = resolvedMediaImages.filter(Boolean);
          setMediaImages(validMediaImages);
        }
      } catch (error) {
        console.error('Error fetching media images:', error);
      }
    };

    fetchMediaImages();
  }, [gallery]);

  const onUploadDocumentsFileClick = async (data) => {
    const { name, key = '', fileKey = '' } = data || {};
    const keyName = fileKey || key
    const getSecuredURL = await getSecuredUrl(keyName, name);
    const securedURL = URL.createObjectURL(getSecuredURL);
    const newTab = window.open(securedURL, '_blank');
    setTimeout(() => {
      URL.revokeObjectURL(securedURL);
      newTab?.close();
    }, 5000);

    return newTab;
  };

  const extractAndGroupObjectivesOutcome = data => {
    return data?.map(({ name: title, objectivesOutcome, objectiveUNSDG }, index) => ({
      key: index,
      label: <StyledText as="p" variant="B3d">{title}</StyledText>,
      children: objectivesOutcome?.length > 0 && objectivesOutcome?.map(({ name, outcomeDescription, outcomeApproach, outcomeUNSDG }) => {
        return <button key={outcomeDescription} type="button" className={`collapseChildContainer ${selectedName === name ? 'selectedName' : ''}`} onClick={() => {
          setSelectedUnsdgs([
            ...(objectiveUNSDG ?? []),
            ...(outcomeUNSDG ?? [])
          ]); setSelectedName(name)
        }}><StyledText as="p" variant="B3d">{name}</StyledText><StyledText as="p" variant="B3">{outcomeApproach || outcomeDescription}</StyledText></button>
      })
    }));
  }

  const impactStrategyOutcomes = extractAndGroupObjectivesOutcome(objectives);

  return (
    <div className={baseClassName}>
      {(!opportunityData || showSpinner) && (
        <div className="overlay">
          <Spin className="spinner" size="large" spinning={!programData || showSpinner} />
        </div>
      )}
      <div className="leftSubContainer">
        <div className="subContainer">
          <StyledText as="p" variant="H2">
            {intl.formatMessage({ id: "summary_title" })}
          </StyledText>
          <StyledText as="p" variant="B3">
            {opportunitySummary}
          </StyledText>
        </div>
        <div className="subContainer">
          <StyledText as="p" variant="H2">
            {intl.formatMessage({ id: "support_awarded" })}
          </StyledText>
          <div className="supportFlexContainer">
            {Grant?.length > 0 && <div className="supportContainer grantContainer">
              <div className="titleContainer">
                <div>
                  <Avatar src={grantIcon} />
                </div>
                <div>
                  <StyledText as="p" variant="H3">
                    {intl.formatMessage({ id: "grants" })}
                  </StyledText>
                </div>
              </div>
              <div className="flexBox">
                <div className="flexBoxChild">
                  <div className="flexColumn">
                    {Grant?.map((el) => <StyledText key={el?.donationAmount?.trim()} as="p" variant="B3c">
                      {el?.currencySymbol || '$'}
                      {el?.donationAmount?.trim()}
                    </StyledText>)}
                  </div>
                </div>
              </div>
            </div>
            }
            {Volunteering?.length > 0 && <div className="supportContainer volunteeringContainer">
              <div className="titleContainer">
                <div>
                  <Avatar src={volunteer} />
                </div>
                <div>
                  <StyledText as="p" variant="H3">
                    {intl.formatMessage({ id: "volunteering" })}
                  </StyledText>
                </div>
              </div>
              <ul>
                {Volunteering.map((el) => (
                  <li key={el?.donationAmount}>
                    <StyledText as="span" variant="B3c">
                      {`${el?.donationAmount} ${intl.formatMessage({ id: "days" })}`}
                    </StyledText>
                  </li>
                ))}
              </ul>
            </div>}
            {InKind?.length > 0 && <div className="supportContainer inKindContainer">
              <div className="titleContainer">
                <div>
                  <Avatar src={inkind} />
                </div>
                <div>
                  <StyledText as="p" variant="H3">
                    {intl.formatMessage({ id: "inKind" })}
                  </StyledText>
                </div>
              </div>
              <ul>
                {InKind?.map((el) => (
                  <li key={el?.inKindActivityDescription}>
                    <StyledText as="span" variant="B3">
                      {`${el?.inkindDescription} ${intl.formatMessage({ id: "of_equivalent_value" })}`}
                      <StyledText as="span" variant="B3c">
                        {` ${el.currencySymbol}${el?.donationAmount}`}
                      </StyledText>
                    </StyledText>
                  </li>
                ))}
              </ul>
            </div>}
          </div>
        </div>
        <div className="titleContainer switchItemContainer">
          <div>
            <StyledText as="p" variant="B3">{intl.formatMessage({ id: "show_planned_efforts" })}</StyledText>
          </div>
          <Switch className="switchItem" onChange={() => setShowPlanned(prev => !prev)} />
        </div>
        <Divider />
        <div className="subContainer">
          <StyledText as="p" variant="H2">
            {intl.formatMessage({ id: "inputs" })}
          </StyledText>
          <div className="supportParentContainer">
            {(actualDonationUse?.length > 0 || actualVolunteerUse?.length > 0 || actualInKindUse?.length > 0) && <div className="whiteSupportContainer volunteeringContainer">
              <div className="titleContainer">
                <div> <StyledText as="p" style={{ marginRight: '5px' }} variant="B3d">
                  {intl.formatMessage({ id: "actual" })}
                </StyledText>
                </div>
                <div>
                  {inputsChange === 'Y' ? <StyledText as="p" variant="B3b">
                    {intl.formatMessage({ id: "includes_changes" })}
                  </StyledText> :
                    <StyledText as="p" variant="B3h">{intl.formatMessage({ id: "hundred_as_planned" })}<CheckOutlined style={{ marginLeft: '5px' }} /></StyledText>}
                </div>
              </div>
              {actualDonationUse?.map((el) => <div key={el.numberOfItems} className="textContainer"><StyledText key={el.numberOfItems} as="p" variant="B3">
                {`For ${el.numberOfItems} ${el.itemDescription}`}</StyledText>
                <StyledText as="p" variant="B3d">{cashTargetCurrencyCode}{el.monetaryUnit}</StyledText></div>)}
              {actualVolunteerUse?.map((el) => el?.volunteerUnit && el?.volunteerValue && <StyledText key={el.volunteerValue} as="p" variant="B3">
                {`${el.volunteerValue} volunteering for ${el.volunteerUnit}`}</StyledText>)}
              {actualInKindUse?.map((el) => <StyledText key={el.inKindOfferingDescription} as="p" variant="B3">
                {`${el.inKindOfferingDescription} for ${el.inKindActivityDescription}`}</StyledText>)}
            </div>}
            {showPlanned && environmentalMeasure?.length > 0 && <div className="whiteSupportContainer volunteeringContainer plannedContainer">
              <div className="titleContainer">
                <div> <StyledText as="span" variant="B3d">
                  {intl.formatMessage({ id: "planned" })}
                </StyledText>
                </div>
              </div>
              {donationUse?.map((el) => <div key={el.numberOfItems} className="textContainer"><StyledText key={el.numberOfItems} as="p" variant="B3">
                {`For ${el.numberOfItems} ${el.itemDescription}`}</StyledText>
                <StyledText as="p" variant="B3d">{cashTargetCurrencyCode}{el.monetaryUnit}</StyledText></div>)}
              {volunteerUse?.map((el) => el?.volunteerUnit?.trim() && el?.volunteerValue?.trim() && <StyledText key={el.volunteerValue} as="p" variant="B3">
                {`${el.volunteerValue} volunteering for ${el.volunteerUnit}`}</StyledText>)}
              {inKindUse?.map((el) => <StyledText key={el.inKindOfferingDescription} as="p" variant="B3">
                {`${el.inKindOfferingDescription} for ${el.inKindActivityDescription}`}</StyledText>)}
            </div>}
          </div>
        </div>
        <div className="subContainer">
          <StyledText as="p" variant="H2">
            {intl.formatMessage({ id: "outputs" })}
          </StyledText>
          <div className="supportParentContainer">
            {totalOutputs?.length > 0 && <div className="whiteSupportContainer volunteeringContainer">
              <div className="titleContainer">
                <div> <StyledText as="p" style={{ marginRight: '5px' }} variant="B3d">
                  {intl.formatMessage({ id: "actual" })}
                </StyledText>
                </div>
                <div>
                  {differentOutputs === 'Y' ? <StyledText as="p" variant="B3b">
                    {intl.formatMessage({ id: "includes_changes" })}
                  </StyledText> :
                    <StyledText as="p" variant="B3h">{intl.formatMessage({ id: "hundred_as_planned" })}<CheckOutlined style={{ marginLeft: '5px' }} /></StyledText>}
                </div>
              </div>
              <ul>{totalOutputs?.map((el) => <li key={el}><StyledText as="span" variant="B3">{`${el?.outputNumber} ${el?.outputDescription}`}</StyledText></li>)}</ul>
            </div>}
            {showPlanned && totalOutputs?.length > 0 && <div className="whiteSupportContainer volunteeringContainer plannedContainer">
              <div className="titleContainer">
                <div> <StyledText as="span" variant="B3d">
                  {intl.formatMessage({ id: "planned" })}
                </StyledText>
                </div>
              </div>
              <ul>{expectedOutput?.map((el) => <li key={el}><StyledText as="span" variant="B3">{`${el?.outputNumber} ${el?.outputDescription}`}</StyledText></li>)}</ul>
            </div>}
          </div>
        </div>
        {beneficiariesType?.length > 0 && <div className="subContainer">
          <StyledText as="p" variant="H2">
            {intl.formatMessage({ id: "beneficiaries" })}
          </StyledText>
          <div className="titleContainer">
            <div><Avatar src={socialIcon} /></div>
            <div>
              <StyledText as="p" variant="H3">
                {intl.formatMessage({ id: "social_beneficiaries" })}
              </StyledText>
            </div>
          </div>
          <div className="supportParentContainer">
            {Array.isArray(beneficiariesType) && beneficiariesType?.length > 0 && <div className="whiteSupportContainer volunteeringContainer">
              <div className="titleContainer">
                <div> <StyledText as="p" style={{ marginRight: '5px' }} variant="B3d">
                  {intl.formatMessage({ id: "actual" })}
                </StyledText>
                </div>
                <div>
                  {beneficiariesChanged === 'Y' ? <StyledText as="p" variant="B3b">
                    {intl.formatMessage({ id: "includes_changes" })}
                  </StyledText> :
                    <StyledText as="p" variant="B3h">{intl.formatMessage({ id: "hundred_as_planned" })}<CheckOutlined style={{ marginLeft: '5px' }} /></StyledText>}
                </div>
              </div>
              <ul>{beneficiariesType.map((el) => <li key={el}><StyledText as="span" variant="B3">{el}</StyledText></li>)}</ul>
              <HorizontalGraph
                name={intl.formatMessage({
                  id: 'direct',
                })}
                percentage={Number(
                  totalDirect
                )}
                maxValue={Math.max(Number(
                  totalDirect
                ), Number(
                  totalIndirect
                ))}
                background="#56D9FE"
              />

              <HorizontalGraph
                name={intl.formatMessage({
                  id: 'indirect',
                })}
                percentage={Number(
                  totalIndirect
                )}
                maxValue={Math.max(Number(
                  totalDirect
                ), Number(
                  totalIndirect
                ))}
                background="#A4A1FB"
              />
            </div>
            }
            {showPlanned && Array.isArray(opportunityBeneficiaries) && opportunityBeneficiaries?.length > 0 && <div className="whiteSupportContainer volunteeringContainer plannedContainer">
              <div className="titleContainer">
                <div> <StyledText as="span" variant="B3d">
                  {intl.formatMessage({ id: "planned" })}
                </StyledText>
                </div>
              </div>
              <ul>{opportunityBeneficiaries.map((el) => <li key={el}><StyledText as="span" variant="B3">{el}</StyledText></li>)}</ul>
              <HorizontalGraph
                name={intl.formatMessage({
                  id: 'direct',
                })}
                percentage={Number(
                  directBeneficiaries
                )}
                maxValue={Math.max(Number(
                  directBeneficiaries
                ), Number(
                  indirectBeneficiaries
                ))}
                background="#56D9FE"
              />

              <HorizontalGraph
                name={intl.formatMessage({
                  id: 'indirect',
                })}
                percentage={Number(
                  indirectBeneficiaries
                )}
                maxValue={Math.max(Number(
                  directBeneficiaries
                ), Number(
                  indirectBeneficiaries
                ))}
                background="#A4A1FB"
              />
            </div>
            }
          </div>
        </div>}
        {actualEnvironmentalMeasure?.length > 0 && <div className="subContainer">
          <div className="titleContainer">
            <div><Avatar src={environmentIcon} /></div>
            <div>
              <StyledText as="p" variant="H3">
                {intl.formatMessage({ id: "environmental_beneficiaries" })}
              </StyledText>
            </div>
          </div>
          <div className="supportParentContainer">
            {actualEnvironmentalMeasure?.length > 0 && <div className="whiteSupportContainer volunteeringContainer">
              <div className="titleContainer">
                <div> <StyledText as="p" style={{ marginRight: '5px' }} variant="B3d">
                  {intl.formatMessage({ id: "actual" })}
                </StyledText>
                </div>
                <div>
                  {environmentMeasuresChange === 'Y' ? <StyledText as="p" variant="B3b">
                    {intl.formatMessage({ id: "includes_changes" })}
                  </StyledText> :
                    <StyledText as="p" variant="B3h">{intl.formatMessage({ id: "hundred_as_planned" })}<CheckOutlined style={{ marginLeft: '5px' }} /></StyledText>}
                </div>
              </div>
              <ul>{actualEnvironmentalMeasure?.map((el) => <li key={el}><StyledText as="span" variant="B3"><span>{el?.amount}</span>{` ${el?.unit} ${el?.unit === 'Hectares' ? 'of ' : ''}${el?.subCategory} ${el?.category === 'Habitats' ? `${intl.formatMessage({ id: 'habitats' })} ` : ''} ${el?.description}`}</StyledText></li>)}</ul>
            </div>}
            {showPlanned && environmentalMeasure?.length > 0 && <div className="whiteSupportContainer volunteeringContainer plannedContainer">
              <div className="titleContainer">
                <div> <StyledText as="span" variant="B3d">
                  {intl.formatMessage({ id: "planned" })}
                </StyledText>
                </div>
              </div>
              <ul>{environmentalMeasure?.map((el) => <li key={el}><StyledText as="span" variant="B3"><span>{el?.amount}</span>{` ${el?.unit} ${el?.unit === 'Hectares' ? 'of ' : ''}${el?.subCategory} ${el?.category === 'Habitats' ? `${intl.formatMessage({ id: 'habitats' })} ` : ''} ${el?.description}`}</StyledText></li>)}</ul>
            </div>}
          </div>
        </div>}
        {Array.isArray(indicators) && indicators?.length > 0 && <div className="subContainer">
          <StyledText as="p" variant="H2">
            {intl.formatMessage({ id: "indicators" })}
          </StyledText>
          <CharityFinalPreviewIndicators />
        </div>}
        <div className="subContainer">
          <StyledText as="p" variant="H2">
            {intl.formatMessage({ id: "objectives_and_outcomes" })}
          </StyledText>
          {impactStrategyOutcomes?.length > 0 && <div className="subContainer">
            {isProgramCustom && <button className="expandAll" onClick={() => setExpandAll(prev => !prev)}>
              {intl.formatMessage({ id: "expand_all" })}
            </button>}
            <div className="unsdgPartitionContainer">
              <div className="unsdgsContainerContainer">
                <StyledText as="p" variant="H2">
                  {intl.formatMessage({ id: "planned" })}
                </StyledText>
                <div className="unsdgsContainer">
                  <button className="showAllGoals" onClick={() => { setSelectedUnsdgs([]); setSelectedName('') }}><div><CheckOutlined /></div><StyledText as="p" variant="B3">{intl.formatMessage({ id: "show_all_goals" })}</StyledText></button>{goals.map((el) => <Avatar key={el.image} src={selectedUnsdgs?.includes(el.key) ? el.selected : el.image} shape="square" className="unsdgs" />)}
                </div>
              </div>
              <div className="collapseContainer">
                {<Collapse items={impactStrategyOutcomes} activeKey={expandAll ? impactStrategyOutcomes.map(({ key }) => key) : undefined} expandIconPosition="end" />}
              </div>
            </div>
          </div>
          }
          <div className="subWhiteContainer">
            <StyledText as="p" variant="H2">
              {intl.formatMessage({ id: "actual" })}
            </StyledText>
            <StyledText as="p" variant="B3d">
              {intl.formatMessage({ id: "were_you_successful_in_achieving_your_objectives_and_outcomes" })}
            </StyledText>
            <StyledText as="p" variant="B3">
              {typeof goalofCompanyProgramme === 'string' ? ReactHtmlParser(goalofCompanyProgramme) : goalofCompanyProgramme}
            </StyledText>
          </div>
        </div>
        <div className="subContainer">
          <StyledText as="p" variant="H2">
            {intl.formatMessage({ id: "additional_details" })}
          </StyledText>
          <div className="subWhiteContainer">
            <StyledText as="p" variant="H3">
              {intl.formatMessage({ id: "next_steps" })}
            </StyledText>
            <StyledText as="p" variant="B3">
              {typeof whatHappensNext === 'string' ? ReactHtmlParser(whatHappensNext) : whatHappensNext}
            </StyledText>
          </div>
          <div className="subWhiteContainer">
            <StyledText as="p" variant="H3">
              {intl.formatMessage({ id: "comments" })}
            </StyledText>
            <StyledText as="p" variant="B3">
              {typeof additionalComment === 'string' ? ReactHtmlParser(additionalComment) : additionalComment}
            </StyledText>
          </div>
        </div>
        {Array.isArray(customContentAnswer) && customContentAnswer?.length > 0 && <div className="subContainer">
          <StyledText as="p" variant="H2">
            {intl.formatMessage({ id: "company_questions" })}
          </StyledText>
          {customContentAnswer?.map((el) => <div key={el?.customContentQuestionDes} className="subWhiteContainer">
            <StyledText as="p" variant="B3d">
              {el?.customContentQuestionDes}
            </StyledText>
            <StyledText as="p" variant="B3">
              {el?.customContentAnswerDes}
            </StyledText>
          </div>)}
        </div>}
        {Array.isArray(caseStudy) && caseStudy?.length > 0 && <div className="subContainer">
          <StyledText as="p" variant="H2">
            {intl.formatMessage({ id: "case_studies" })}
          </StyledText>
          {caseStudy?.map((el) => (
            <div className="subWhiteContainer" key={el?.outcome || el?.name}>
              <StyledText
                as="p"
                variant="B3c"
                style={{
                  background: '#CBEAFF',
                  borderRadius: '4px',
                  width: 'max-content',
                  paddingInline: '4px',
                }}
              >
                {`${intl.formatMessage({ id: 'case_study' })}`}
              </StyledText>
              <div className="contentContainer">
                <div className="leftContent">
                  <StyledText as="p" variant="B3" style={{ fontSize: '16px', color: '#759CC7' }}>
                    {el.name}
                  </StyledText>
                  <StyledText as="p" variant="B3d">
                    {intl.formatMessage({ id: 'description' })}
                  </StyledText>
                  <StyledText as="p" variant="B3">
                    {el?.outcome}
                  </StyledText>
                  {(el?.shareQuote || el?.quoteBy) && <div className="quoteContainer">
                    <StyledText as="p" variant="B3" style={{ fontStyle: 'italic' }}>
                      {el?.shareQuote}
                    </StyledText>
                    <StyledText as="p" variant="B3" style={{ fontStyle: 'italic' }}>
                      {el?.quoteBy}
                    </StyledText>
                  </div>}
                </div>
                <div className="mediaContainer">
                  {el?.outcome && documentUrls[el.outcome]?.length === el?.documents?.length && (
                    <MediaCarousel data={documentUrls[el.outcome]} />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>}

        {Array.isArray(gallery) && gallery?.length > 0 && <div className="subContainer mediaSection">
          <StyledText as="p" variant="H2">
            {intl.formatMessage({ id: "media" })}
          </StyledText>
          {mediaImages?.length === gallery?.length && <MediaCarousel data={mediaImages} />}
        </div>}
        {((Array.isArray(uploadedDocuments) && uploadedDocuments?.length > 0)) && <div className="subContainer">
          <StyledText as="p" variant="H2">
            {intl.formatMessage({ id: "additional_files" })}
          </StyledText>
          {<div className="fileContainer">
            {(uploadedDocuments)?.map((el) => <button
              className="subDocumentsContainer"
              key={el.uid}
              onClick={() => onUploadDocumentsFileClick(el)}
            >
              <div>
                <Avatar src={file} />
              </div>
              <div> <StyledText as="p" variant="B3" key={el.uid}>
                {el.name}
              </StyledText>
                <StyledText as="p" variant="B3" key={el.uid} className="contentContainer">
                  {el.mediaDescription}
                </StyledText>
              </div>
            </button>)}
          </div>}
        </div>}
      </div>
    </div>
  );
};

const StyledProgramReview = styled(ProgramReview)`
  &.programReview {
    display: flex;
    gap: 24px;
    padding-bottom: 60px;
    width: 90%;

    .textContainer{
      display: flex;
      flex-direction: column;
    }

    .subDocumentsContainer{
        display: flex;
        gap: 5px; 
        align-items: center;
        cursor: pointer;
        background: transparent;
        border: 0;
        outline: none;
        background: white;
        border-radius: 8px;
        width: 35vw;
        padding: 16px;
        align-items: center;
        .contentContainer{
            display: flex;
            gap: 5px; 
            align-items: center;
        }
    }

    .fileContainer {
      display: flex;
      gap: 20px;
        .fileKey{
            background: white;
            padding: 15px;
            border-radius: 10px;
            box-shadow: 0px 3px 16px #0000000D;
            .ant-avatar-image {
                cursor: pointer;
            }
        }
    }

    .supportParentContainer{
      display: flex;
      gap: 10px;
    }

    .plannedContainer {
      background: #F3F3F366 !important;
      p{
        opacity: 0.5;
      }
    }
    .switchItemContainer{
      width: 100%;
      display: flex;
      justify-content: flex-end;

      .switchItem{
      width: max-content;
      }
    }

    .quoteContainer{
      border-left: 2px solid black;
      padding: 5px 10px;
    }

    .contentContainer{
      display: flex;
      justify-content: space-between;
      .leftContent{
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .mediaContainer{
        width: 20vw;
        .carouselImage {
      width: 100%;
      height: 150px;
      object-fit: contain;
    }
        .ant-carousel, .slick-initialized  {
          height: 150px;
        }
        .ant-carousel .slick-dots-bottom {
          bottom: 0 !important;
        }
      }
    }

    .overlay {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 1000;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.45);
    }
        .spinner {
        z-index: 2000;
        margin: auto;
        left: 50%;
        right: 50%;
        position: absolute;
        bottom: 50%;
        }
            ul {
            margin: 0;
            }

    .leftSubContainer{
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 30px;
    } 

    .whiteSupportContainer{
        background: white;
        box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px;
          width: 35vw;
          &.volunteeringContainer {
         box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
    }

    .ant-collapse-expand-icon {
      color: #313131 !important;
    }

    .mediaSection{
        margin-bottom: 20px;
    }

    .flexBox{
        display: flex;
        align-items: flex-start;
        gap: 20px;
        margin-left: 35px;
        &.justifyContent{
            justify-content: flex-start;
        }
        .flexBoxChild{
            display: flex;
            flex-direction: column;
            gap: 10px;
            .flexColumn{
                display: flex;
                flex-direction: column;
                .noBreak{
                    white-space: nowrap;
                }
            }
        }
    }

    .divider{
        height: 100%;
    margin-inline: 10px;
    }
    .outputNumber{
        background: #dee3ed;
        border-radius: 5px;
        padding: 1px 8px;
        border: 1px solid grey; 
    }

    .supportFlexContainer {
      display: flex;
      gap: 30px;
      flex-direction: column;

      .supportContainer {
        padding: 16px;
        width: max-content;
        width: 35vw;

        &.grantContainer {
          background: transparent linear-gradient(223deg, #ffffff 0%, #b2f9bd 100%) 0% 0% no-repeat padding-box;
          box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 0px;
        }

        &.volunteeringContainer {
          background: transparent linear-gradient(223deg, #ffffff 0%, #8bdaff 100%) 0% 0% no-repeat padding-box;
          box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 0px;
        }

        &.inKindContainer {
          background: transparent linear-gradient(223deg, #ffffff 0%, #ffaffa 100%) 0% 0% no-repeat padding-box;
          box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
        }

        
      }
    }
    .titleContainer {
          display: flex;
          gap: 2px;
          align-items: center;
        }
    }
    .helperText {
    color: #5271FF !important;
  }

    .subContainer {
      display: flex;
      flex-direction: column;
      gap: 10px;
      .descriptionContainer{
        max-width: 70ch;
            word-wrap: break-word;
            overflow-wrap: break-word;
        p, li{
            font-size: 14px;
            font-weight: 500;
            color: #242424;
            font-family: sans-serif;
            max-width: 70ch;
            word-wrap: break-word;
            overflow-wrap: break-word;
        }
      }
    }

    .subWhiteContainer{
      display: flex;
      flex-direction: column;
      gap: 10px;
      background: white;
      border-radius: 10px;
      padding: 10px;
    }

    .ant-collapse-expand-icon {
      transform: rotate(90deg) translate(-5px);
      svg{
            fill: #313131 !important;
        }
    }

    .ant-collapse-item-active {
      .ant-collapse-expand-icon {
        transform: rotate(0deg) translate(-5px);
      }
    }

    .ant-timeline-item-head {
      background: transparent !important;
    }

    .anticon-check {
      color: #58b55e;
    }
    .supportContainer {
    padding: 16px;
    width: max-content;
    width: 35vw;
    .divider{
        height: 100%;
    margin-inline: 10px;
    }
    &.grantContainer {
      background: transparent linear-gradient(223deg, #ffffff 0%, #b2f9bd 100%) 0% 0% no-repeat padding-box;
      box-shadow: 0px 3px 16px #0000000d;
      border-radius: 10px;
      display: flex;
      flex-direction: column;
      gap: 0px;
    }
  }
  .additionalFunding{
        background: white !important;
        padding: 20px;
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        gap: 20px;
    }
  .expenditureValues{
        background: #5DB76A;
        border-radius: 5px;
        padding: 1px 8px;
        border: 1px solid #5DB76A;
    }
    .supportContainer {
    padding: 16px;
    width: max-content;
    width: 35vw;
    .divider{
        height: 100%;
    margin-inline: 10px;
    }
    &.volunteeringContainer {
          background: transparent linear-gradient(223deg, #ffffff 0%, #8bdaff 100%) 0% 0% no-repeat padding-box;
          box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 0px;
        }
  }
  .titleContainer {
          display: flex;
          gap: 2px;
          align-items: center;
        }
    .supportContainer {
    padding: 16px;
    width: max-content;
    width: 35vw;
        &.inKindContainer {
          background: transparent linear-gradient(223deg, #ffffff 0%, #ffaffa 100%) 0% 0% no-repeat padding-box;
          box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
        }
  }
  .outputNumber{
        background: #dee3ed;
        border-radius: 5px;
        padding: 1px 8px;
        border: 1px solid grey; 
    }
    .unsdgPartitionContainer {
      display: flex;
      gap: 20px;
      background: white;
    padding: 30px;
    border-radius: 10px;

      .collapseContainer {
        width: 50%;
        button {
            width: 100%;
        }
      }
      .unsdgsContainerContainer{
        display: flex;
        flex-direction: column;
        gap: 15px;
          width: 48%;
      }
      .unsdgsContainer {
        display: flex;
        gap: 15px;
        flex-wrap: wrap;
        height: max-content;

        .showAllGoals {
          height: 100px;
          width: 100px;
          background: white;
          display: flex;
          flex-direction: column;
          padding: 15px;
          justify-content: center;
          align-items: center;
          text-align: center;
          border: 2px solid #00877a;
          border-radius: 6px;

          p,
          svg {
            color: #00877a !important;
          }
        }

        .unsdgs {
          height: 100px;
          width: 100px;
        }
      }
    }

    .expandAll {
      color: #00877a;
      text-align: end;
      background: transparent;
      border: 0;
      cursor: pointer;
    }

    .ant-collapse{
        border: 0;
        border-bottom: 1px solid #D7DDE1;
        .ant-collapse-header{
        background: white !important;
        }
    }
    .ant-collapse-content-active{
        border-top: 0;
    }
     .collapseChildContainer {
          border: 1px solid #d7dde1;
          padding: 12px 16px;
          margin-bottom: 6px;
          cursor: pointer;
          text-align: left;
          background: white;
          font-size: 14px;
          font-weight: ${constants.FONT_WEIGHT_SEMIBOLD};
          color: ${constants.COLOR_GREY_DARK};
          font-family: ${constants.POPPINS_FONT_FAMILY};
    
          &.selectedName {
            border: 1px solid #04ac9c;
            border-radius: 6px;
            background: #bcfbf5;
          }
        }
    
        .collapsible {
          display: flex;
          flex-direction: column;
    
          .collapseChildContainer {
            background: white;
            border: 0;
            border-bottom: 1px solid #d9d9d9;
            padding: 12px 16px;
            text-align: left;
    
            &.selectedName {
              border: 1px solid #04ac9c;
              background: #bcfbf5;
            }
    
            cursor: pointer;
          }
        }
        .ant-collapse-expand-icon {
      transform: rotate(90deg) translate(-5px);
      svg{
            fill: #313131 !important;
        }
    }

    .ant-collapse-item-active {
      .ant-collapse-expand-icon {
        transform: rotate(0deg) translate(-5px);
      }
    }
  }
`;

export default StyledProgramReview;